import { useTranslation } from 'react-i18next';

import { Flex } from '@amzn/storm-ui';
import { Form } from '@amzn/storm-ui-column-form-kit';

import { ErrorDisplay } from '../../../Shared/components/ErrorDisplay/ErrorDisplay';
import { FormInputField } from '../../../Shared/components/FormInputField/FormInputField';
import { isAdminRole } from '../../../Shared/constants/userRoles';
import { ApiError } from '../../../Shared/interfaces/ApiError';
import { CustomButton } from '../../../UiComponentsLibrary/CustomButton';
import { CustomButtonColor } from '../../../UiComponentsLibrary/CustomButton/constants/CustomButtonColor';
import { CustomCard } from '../../../UiComponentsLibrary/CustomCard';
import { useCreateUser } from '../../hooks/useCreateUser';
import { CreateUserProps } from '../../interfaces/userInterfaces';
import CategoryDropDown from '../CategoryDropDown';
import OrgNameDropDown from '../OrgNameDropDown';
import UserRoleDropDown from '../UserRoleDropDown';
import PrivilegedModeDropDown from '../PrivilegedModeDropDown';

import DeletedUserRestorePopover from './DeletedUserRestorePopover';

const CreateUser = (props: CreateUserProps) => {
  const { t } = useTranslation();
  const {
    categoryDropDownProps,
    isError,
    error,
    onCancel,
    handleSubmit,
    inputFields,
    fieldErrors,
    userRoleDropDownProps,
    formData,
    isUserRoleAccountExecutive,
    orgName,
    setOrgName,
    deletedUserRestorePopover,
    handleRestoreDeletedUser,
    setDeletedUserRestorePopover,
    privilegedMode,
    setPrivilegedMode,
  } = useCreateUser(props);

  const errorMessage =
    error && (error as ApiError)?.statusCode === 500
      ? t('create-user-error')
      : error?.message;

  return (
    <>
      <CustomCard id="create-user-card" title={t('create-user')}>
        {isError && (
          <ErrorDisplay
            error={error as ApiError}
            customMessage={errorMessage}
          />
        )}
        <Form id="create-user-form">
          {inputFields.map((field) => (
            <FormInputField
              key={field.id}
              id={field.id}
              label={field.label}
              value={field.value}
              onChange={field.onChange}
              placeholder={field.placeholder}
              error={fieldErrors[field.id as keyof typeof fieldErrors]}
            />
          ))}
          <UserRoleDropDown {...userRoleDropDownProps} />
          {isUserRoleAccountExecutive && (
            <>
              <OrgNameDropDown
                orgName={orgName}
                setOrgName={setOrgName}
                fieldError={fieldErrors.orgName}
              />
              <CategoryDropDown {...categoryDropDownProps} />
            </>
          )}
          {!isAdminRole(userRoleDropDownProps.userRole) && (
            <PrivilegedModeDropDown
              privilegedMode={privilegedMode}
              setPrivilegedMode={setPrivilegedMode}
            />
          )}
        </Form>
        <Flex justifyContent="flex-end" padding="xsmall" columnGap="small">
          <CustomButton
            id="create-user-cancel-button"
            text={t('cancel-button')}
            backgroundColor={CustomButtonColor.cancel}
            handleOnClickCb={onCancel}
          />

          {/* Restore Deleted User Popover */}
          <DeletedUserRestorePopover
            isOpen={deletedUserRestorePopover}
            onClose={() => setDeletedUserRestorePopover(false)}
            userId={formData.emailId.split('@')[0]}
            onRestore={handleRestoreDeletedUser}
            trigger={
              <CustomButton
                id="create-user-submit-button"
                text={t('submit-button')}
                backgroundColor={CustomButtonColor.submit}
                handleOnClickCb={handleSubmit}
              />
            }
          />
        </Flex>
      </CustomCard>
    </>
  );
};

export default CreateUser;
